'use client';
import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useT, useSite } from '../lib/i18n';
import { pickName } from '../lib/i18n-core';
import type { SiteContext, MenuNode, UtilItem } from '../lib/site';
import { Icon } from './Icon';
import { MobileDrawer } from './MobileDrawer';
import { LeftNav } from './LeftNav';
import { useLayerLock } from '../lib/useLayerLock';
import { SmartLink } from './SmartLink';
import { openPalette } from './CommandPalette';
import { useCartCount } from './cartCount';

/**
 * 프론트 헤더 — 설정>테마의 두 값을 그대로 따른다(2026-08-21 사용자 확정).
 *  ① **헤더 디자인**(`headerStyle`) — 가이드 header-guide.html 의 3종 배치.
 *     관리자 썸네일 기준 1=2줄 기본형(A)·2=1줄 풀형(C)·3=2줄 센터형(B).
 *  ② **헤더 우측 메뉴**(`headerUtils`) — 로그인 전/후 각각의 순서·노출.
 *  ③ **전체 메뉴 보기**(`menuAllEnabled`) — 햄버거 + 사이트맵. **헤더 디자인과 무관**하게
 *     3종 모두에 붙는다(2026-08-22 사용자 확인 — 설정이 A 형에서만 먹고 있었다).
 * 마크업·클래스는 가이드 그대로다(header-a/b/c) — 화면이 임의로 정하는 것이 없다.
 */
const STYLE_CLASS: Record<number, 'header-a' | 'header-b' | 'header-c'> = { 1: 'header-a', 2: 'header-c', 3: 'header-b' };

/**
 * 그릴 수 있는 유틸 항목과 기본 순서 — **관리자 HeaderUtilEditor 의 UTIL_ITEMS 와 같은 목록**이다.
 * 저장값에 없는 항목은 노출로 뒤에 붙인다 — 항목이 늘어난 뒤에도 관리자가 다시 저장할 때까지
 * 프론트에서 사라져 있지 않게(관리자 `normalizeUtils` 와 같은 규칙).
 */
const UTIL_ORDER = {
  guest: ['login', 'join', 'search', 'cs'],
  member: ['name', 'myclass', 'logout', 'cart', 'search'],
} as const;

function normalizeUtils(saved: UtilItem[] | undefined, scope: keyof typeof UTIL_ORDER): UtilItem[] {
  const known = UTIL_ORDER[scope] as readonly string[];
  const kept = (saved ?? [])
    .filter((r) => known.includes(r.key))
    .map((r) => ({ key: r.key, visible: r.visible !== false }));
  const missing = known.filter((k) => !kept.some((r) => r.key === k)).map((k) => ({ key: k, visible: true }));
  return [...kept, ...missing];
}

export function Header({ site, menus, loggedIn, memberName, gnavClosed }: {
  site: SiteContext;
  menus: MenuNode[];
  loggedIn: boolean;
  /** 로그인한 회원 이름 — 우측 유틸의 `name` 자리에 그린다(없으면 아이디) */
  memberName: string | null;
  /** 레프트바가 접힌 채로 시작하나 — 서버가 쿠키에서 읽어 준다(첫 그림부터 맞춘다) */
  gnavClosed: boolean;
}) {
  const t = useT();
  const { lang } = useSite();
  const cartCount = useCartCount(loggedIn);
  const [drawer, setDrawer] = useState(false); // 모바일 햄버거
  const [sitemap, setSitemap] = useState(false); // 전체 메뉴(웹 햄버거)
  const [gnavOpen, setGnavOpen] = useState(false); // 레프트바 — 좁은 화면의 헤더 아래 아코디언
  const headerRef = useRef<HTMLElement>(null);
  /**
   * 사이트맵이 시작할 높이 — 가이드 CSS 는 `top: 96px`(2줄 헤더 기준)로 박아 뒀다.
   * 1줄 헤더(C 형)나 상단 배너가 있는 마켓에서는 어긋나므로 열 때 실제 헤더 아래를 잰다.
   */
  const [sitemapTop, setSitemapTop] = useState<number | null>(null);

  /**
   * 펼친 메뉴는 **화면을 옮기면 닫는다**(2026-09-01 사용자 지적 — PC·모바일 공통).
   *
   * 생 `<a>` 였을 때는 페이지가 통째로 다시 그려져 저절로 닫혔는데, `SmartLink`(Next `Link`)로
   * 바꾼 뒤로는 컴포넌트가 살아남아 **펼친 채로 남았다**. 화면은 바뀌었는데 메뉴가 덮고 있으니
   * 다시 접어야 내용을 볼 수 있다.
   *
   * 두 가지를 함께 건다 — 링크를 누른 순간(`closeOnLink`)과 주소가 바뀐 뒤(`pathname`).
   * 앞것은 **같은 주소를 다시 누른 경우**(주소가 안 바뀌어 아래 효과가 안 돈다)를,
   * 뒷것은 **뒤로/앞으로 가기**처럼 링크를 거치지 않는 이동을 맡는다.
   */
  const pathname = usePathname();
  useEffect(() => { setDrawer(false); setSitemap(false); }, [pathname]);
  /** 펼친 메뉴 안에서 링크를 눌렀나 — 껍데기 한 곳에 걸어 링크마다 붙이지 않는다 */
  const closeOnLink = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) { setDrawer(false); setSitemap(false); }
  };

  // 사이트맵을 열 때(그리고 창 크기가 바뀔 때) 헤더 아래 위치를 다시 잰다
  useEffect(() => {
    if (!sitemap) return;
    const measure = () => setSitemapTop(headerRef.current?.getBoundingClientRect().bottom ?? null);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [sitemap]);

/* 서랍·전체 메뉴가 열려 있는 동안 **뒤 화면은 굴러가지 않는다** — 레이어와 같은 규칙(2026-09-03) */
  useLayerLock(drawer, () => setDrawer(false));
  useLayerLock(sitemap, () => setSitemap(false));

  const styleClass = STYLE_CLASS[site.headerStyle] ?? 'header-a';
  const top = menus.filter((m) => m.showTop);

  /** 유틸 구성 — 설정>테마의 순서·노출. 관리자와 **같은 규칙**으로 고른다(normalizeUtils) */
  const utils: UtilItem[] = normalizeUtils(
    loggedIn ? site.headerUtils?.member : site.headerUtils?.guest,
    loggedIn ? 'member' : 'guest',
  ).filter((u) => u.visible);

  /** 아이콘으로 그리는 유틸 — 글자 버튼과 구분선으로 갈린다 */
  const ICON_UTILS = new Set(['search', 'cart']);

  /* 회원명은 **내 정보**로 간다(2026-08-22 사용자 확정) — 강의실은 따로 항목이 있다 */
  const UTIL_HREF: Record<string, string> = {
    cs: '/boards/faq', join: '/join', login: '/login', logout: '/logout',
    name: '/my/profile', myclass: '/my',
  };
  /*
   * 유틸 라벨. `name` 은 **회원 이름**이다(2026-08-22 사용자 지적: 상태명 "일반" 이 나왔다) —
   * 관리자 설정의 항목 이름도 "회원명" 이다. 이름이 없으면 아이디로 떨어진다.
   */
  const utilLabel = (key: string) =>
    ({
      cs: t('shell.cs'), join: t('shell.join'), login: t('shell.login'), logout: t('shell.logout'),
      name: memberName ?? '', myclass: t('my.classroom'),
    })[key] ?? key;

  const utilEl = (u: UtilItem) => {
    if (u.key === 'name' && !memberName) return null; // 이름을 모르면 빈 링크를 남기지 않는다
    /*
     * 검색 — **돋보기 아이콘 하나**다(2026-08-23 사용자 확정). 누르면 명령 팔레트(⌘K)가 열린다.
     * 예전에는 가이드의 `srch-btn`(230px 알약에 안내 문구)을 A·B 배치의 메뉴 줄 옆에 그렸는데,
     * 검색을 팔레트가 맡게 되면서 그 입력칸 모양은 뜻이 없어졌다 — 다른 유틸과 같은 아이콘
     * 버튼으로 두면 **설정>테마>헤더>우측 메뉴의 순서**도 그대로 따른다.
     */
    if (u.key === 'search') return (
      <button key={u.key} type="button" className="header-btn" onClick={() => openPalette()}
        aria-label={t('shell.search')}>
        <Icon name="search" />
      </button>
    );
    if (u.key === 'cart') return (
      <SmartLink key={u.key} href="/cart" className="header-btn" aria-label={t('shell.cart')}>
        {/* 점은 **담긴 것이 있을 때만**(2026-08-28 사용자 지시) — 비어 있는데 붙어 있으면 거짓말이다 */}
        <Icon name="shopping-cart" />{cartCount > 0 && <span className="cart-dot"></span>}
      </SmartLink>
    );
    return <SmartLink key={u.key} href={UTIL_HREF[u.key]} className="header-btn txt">{utilLabel(u.key)}</SmartLink>;
  };

  /*
   * 유틸 묶음. 가이드는 **아이콘 묶음과 글자 묶음 사이**에 구분선을 하나 둔다
   * (A 형 `장바구니|sep|고객센터…`, C 형 `검색 장바구니|sep|고객센터…`. B 형은 구분선이 없다).
   * 그래서 마지막 아이콘 항목 뒤에 넣는다 — 관리자가 순서를 바꿔도 규칙이 유지된다.
   */
  const utilRow = (withSep: boolean) => {
    const lastIcon = utils.reduce((acc, u, i) => (ICON_UTILS.has(u.key) ? i : acc), -1);
    return utils.flatMap((u, i) => {
      const el = utilEl(u);
      if (!el) return [];
      return withSep && i === lastIcon && i < utils.length - 1
        ? [el, <div key={`${u.key}-sep`} className="sep"></div>]
        : [el];
    });
  };

  const burger = (id: string) => (
    <button key={id} className={`mobile_menu${drawer ? ' on' : ''}`} onClick={() => setDrawer(!drawer)} aria-label={t('shell.allMenu')}>
      <span></span><span></span><span></span>
    </button>
  );

  const navEl = (
    <nav className="nav">
      {/* 전체 메뉴 버튼(웹 햄버거) — 가이드는 nav 맨 앞에 둔다(A 형에 "옵션" 으로 표시).
          설정 값이 켜져 있으면 헤더 3종 모두에 붙는다 — 세 배치 모두 `.nav` 가 있다 */}
      {site.menuAllEnabled && (
        <div className="nav-item">
          <button className={`web_menu${sitemap ? ' on' : ''}`} onClick={() => setSitemap(!sitemap)} aria-label={t('shell.allMenu')}>
            <span></span><span></span><span></span>
          </button>
        </div>
      )}
      {top.map((m) => (
        <div key={m.id} className="nav-item">
          <span className="nav-link">
            <SmartLink href={m.url} target={m.openNewWindow ? '_blank' : undefined}>{pickName(m.name, lang)}</SmartLink>
            {m.children.length > 0 && <Icon name="chevron-down" />}
          </span>
          {m.children.length > 0 && (
            <div className="dropdown">
              {m.children.map((c) => (
                <SmartLink key={c.id} href={c.url} target={c.openNewWindow ? '_blank' : undefined}>{pickName(c.name, lang)}</SmartLink>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );

  /*
   * 레프트바(설정>기능옵션) — 항목이 있을 때만. 좁은 화면에서는 서랍이 아니라 **헤더 아래
   * 아코디언**이라, 그 여닫이 단추가 로고 옆에 붙는다(가이드 `gnav-header-logo-group`).
   * 넓은 화면에서는 그 단추가 CSS 로 숨고 왼쪽 서랍의 가장자리 탭이 그 일을 맡는다.
   */
  const leftbar = site.leftbar ?? [];
  const hasLeftbar = leftbar.length > 0;

  /** 로고 — 레프트바를 쓰는 마켓은 로고와 여닫이 단추를 한 묶음으로 낸다(가이드) */
  const logoEl = (extra = '') => {
    const link = (
      <SmartLink href="/" className={`logo${extra}`}>
        {site.logo
          ? <img src={site.logo} alt={site.name ?? ''} style={{ maxHeight: 36 }} />
          : (site.name ?? '')}
      </SmartLink>
    );
    if (!hasLeftbar) return link;
    return (
      <div className="gnav-header-logo-group">
        {link}
        <button type="button" className={`gnav-header-toggle${gnavOpen ? ' open' : ''}`}
          onClick={() => setGnavOpen((v) => !v)} aria-expanded={gnavOpen} aria-label={site.name ?? ''}>
          <Icon name="chevron-down" />
        </button>
      </div>
    );
  };

  return (
    <>
      <header className={`header ${styleClass}`} ref={headerRef}>
        {styleClass === 'header-a' && (
          <div className="header-a-html">
            <div className="wrap">
              <div className="header-top">
                {logoEl()}
                <div className="header-util">{utilRow(true)}{burger('a')}</div>
              </div>
              <div className="header-in">{navEl}</div>
            </div>
          </div>
        )}

        {styleClass === 'header-b' && (
          <div className="header-b-html">
            <div className="wrap">
              <div className="header-top">
                <div className="header-util-left"></div>
                {logoEl()}
                <div className="header-util">{utilRow(false)}{burger('b')}</div>
              </div>
              <div className="header-in">
                <div className="header-in-wrap">{navEl}</div>
              </div>
            </div>
          </div>
        )}

        {styleClass === 'header-c' && (
          <div className="header-c-html">
            <div className="wrap">
              <div className="header-in">
                {logoEl(' header-c-logo')}
                {navEl}
                <div className="header-c-right">
                  {utilRow(true)}
                  {burger('c')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 전체 메뉴(웹 햄버거) — 1뎁스를 열로, 하위를 아래로(가이드 sitemap) */}
        {site.menuAllEnabled && (
          <div className={`sitemap${sitemap ? ' on' : ''}`} onClick={closeOnLink}
            style={sitemapTop != null ? { top: sitemapTop } : undefined}>
            <div className="wrap">
              <div className="sitemap-inner">
                {top.map((m) => (
                  <div key={m.id} className="sitemap-col">
                    <p className="sitemap-ttl">{pickName(m.name, lang)}</p>
                    {(m.children.length > 0 ? m.children : [m]).map((c) => (
                      <SmartLink key={c.id} className="sitemap-con" href={c.url} target={c.openNewWindow ? '_blank' : undefined}>
                        {pickName(c.name, lang)}
                      </SmartLink>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/*
        레프트바 — 넓은 화면은 왼쪽 서랍, 좁은 화면은 헤더 아래 아코디언. 항목이 없으면
        아무것도 그리지 않는다(자리도 안 밀린다). 헤더 밖에 두는 이유는 서랍이 화면 전체
        높이를 쓰기 때문이다(`position: fixed`) — 헤더 안에 두면 헤더의 쌓임 맥락에 갇힌다.
      */}
      <LeftNav items={leftbar} siteName={site.name} mobileOpen={gnavOpen} initialClosed={gnavClosed} />

      {/* 모바일 드로어 — 가이드 mobilemenu E안(2026-09-02). 바로가기·요약은 서랍이 스스로 챙긴다 */}
      <MobileDrawer open={drawer} menus={top} quickLinks={site.quickLinks ?? []} loggedIn={loggedIn} onNavigate={closeOnLink} />
    </>
  );
}
